import { useMemo } from 'react';
import { Button } from '~/shared/components/buttons/Button';
import { X } from 'lucide-react';
import { cn } from 'tailwind-variants';
import type { FormFieldModel } from '@generated';
import Yup from '~/util/yup';
import type { FormValues } from '~/shared/forms/types';
import type { FormControlModelExtended } from '../forms/types';
import { useForm } from '~/shared/forms/FormContext';
import { useFormField, type useFormFieldResult } from '~/shared/forms/useFormField';
import { generateValidationSchema } from '../forms/validation';
import { getInitialValues } from '../forms/values';
import { FieldLabelWithHtml } from '~/shared/forms/controls/base-controls/FieldLabelWithHtml';
import { FieldErrorWithoutAria } from '~/shared/forms/controls/base-controls/FieldError';
import { FormModal } from '~/shared/forms/FormModal';
import { FormFields } from '../forms/FormFields';
import { useFormControlsAndFields } from '../forms/FormControlsAndFieldsContext';

function getChildFormFields(formFields: FormFieldModel[], arrayFieldName: string): FormFieldModel[] {
	const parentFormField = formFields.find(f => f.name === arrayFieldName);
	return parentFormField?.formFields ?? [];
}

function getParentContextFields(formFields: FormFieldModel[], arrayFieldName: string): FormFieldModel[] {
	return formFields.filter(f => f.name !== arrayFieldName).map(f => ({ ...f, validationRules: undefined, fieldActions: undefined }));
}

export function BodyStructureControlArray({ controlModel }: { controlModel: FormControlModelExtended }) {
	const fieldProps = useFormField<FormValues[]>({ ...controlModel, name: controlModel.name ?? '' });

	const handleRemove = (index: number) => {
		fieldProps.onChange(fieldProps.value.filter((_, i) => i !== index));
	};

	if (!controlModel.formControls || controlModel.formControls.length === 0) {
		return null;
	}

	const isEmpty = !fieldProps.value || fieldProps.value.length === 0;

	return (
		<div className="flex flex-col gap-1">
			{!controlModel.hideLabel && <FieldLabelWithHtml className="mb-1" label={fieldProps.label} required={fieldProps.required} />}

			{isEmpty ? (
				<Button
					actionType="add"
					appearance="outline"
					renderModal={closeModal => (
						<BodyStructureControlModal controlModel={controlModel} fieldProps={fieldProps} closeModal={closeModal} />
					)}
					isDisabled={fieldProps.isReadOnly}
					label="Add a new location"
				/>
			) : (
				<div className={cn('flex flex-col gap-2', controlModel.className)}>
					{fieldProps.value.map((item, index) => (
						// eslint-disable-next-line react/no-array-index-key
						<div key={index} className="flex items-center gap-2 rounded border p-2">
							<span className="grow text-sm">Location {index + 1}</span>
							{!fieldProps.isReadOnly && (
								<>
									<Button
										actionType="edit"
										appearance="outline"
										renderModal={closeModal => (
											<BodyStructureControlModal
												controlModel={controlModel}
												fieldProps={fieldProps}
												existingItem={{ index, item: fieldProps.value[index] }}
												closeModal={closeModal}
											/>
										)}
										className="w-fit"
										label="Edit"
									/>
									<Button Icon={X} appearance="outline" onPress={() => handleRemove(index)} className="w-fit" label="Remove" />
								</>
							)}
						</div>
					))}
					{!fieldProps.isReadOnly && (
						<Button
							actionType="add"
							appearance="outline"
							renderModal={closeModal => (
								<BodyStructureControlModal controlModel={controlModel} fieldProps={fieldProps} closeModal={closeModal} />
							)}
							className="w-fit"
							label="Add a new location "
						/>
					)}
				</div>
			)}

			{fieldProps.error && <FieldErrorWithoutAria {...fieldProps.error} />}
		</div>
	);
}

function BodyStructureControlModal({
	controlModel,
	existingItem,
	fieldProps,
	closeModal
}: {
	controlModel: FormControlModelExtended;
	existingItem?: {
		item: FormValues;
		index: number;
	};
	fieldProps: useFormFieldResult<FormValues[]>;
	closeModal: () => void;
}) {
	const { formHelpers } = useForm();

	const context = useFormControlsAndFields();
	const formFields = context?.formFields;
	const childFormFields = useMemo(() => getChildFormFields(formFields!, controlModel.name!), [formFields, controlModel.name]);

	const parentContextFields = useMemo(() => getParentContextFields(formFields!, controlModel.name!), [formFields, controlModel.name]);

	const modalFormFields = useMemo(() => [...childFormFields, ...parentContextFields], [childFormFields, parentContextFields]);
	const validationSchema = useMemo(() => {
		const schema = generateValidationSchema(modalFormFields);
		return schema ? Yup.object().shape(schema) : undefined;
	}, [modalFormFields]);

	const handleSave = (itemValues: FormValues) => {
		const childFieldNames = new Set(childFormFields.map(f => f.name));
		const childValues = Object.fromEntries(Object.entries(itemValues).filter(([key]) => childFieldNames.has(key)));

		if (existingItem) {
			const updated = [...fieldProps.value];
			updated[existingItem.index] = childValues;
			fieldProps.onChange(updated);
		} else {
			fieldProps.onChange([...fieldProps.value, childValues]);
		}
		closeModal();
	};

	const getModalInitialValues = (): FormValues => {
		const parentValues = { ...formHelpers.values };
		delete parentValues[controlModel.name!];

		const childDefaults = getInitialValues(childFormFields) ?? {};
		const itemValues = existingItem?.item.values ?? childDefaults;

		return { ...parentValues, ...itemValues };
	};
	return (
		<FormModal
			title={existingItem ? 'Edit Location' : 'Add Location'}
			initialValues={getModalInitialValues()}
			validationSchema={validationSchema}
			onSuccess={handleSave}
			closeModal={closeModal}
		>
			<div className="grid grid-cols-1 gap-4">
				<FormFields formControls={controlModel.formControls!} formFields={modalFormFields} />
			</div>
		</FormModal>
	);
}
